import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const privacyContent = `
Última actualización: abril 2026

En Epikka Store, nos tomamos muy en serio la protección de tus datos personales. Esta política describe cómo recopilamos, usamos y protegemos tu información.

1. Información que recopilamos
Cuando compras nuestra guía digital, recopilamos tu nombre, dirección de correo electrónico y datos de pago necesarios para procesar tu compra. No almacenamos datos de tarjetas de crédito directamente; estos son gestionados por nuestro procesador de pagos seguro.

2. Uso de la información
Utilizamos tu información para:
• Procesar y entregar tu compra digital
• Enviarte el enlace de descarga del ebook y los bonos
• Comunicarnos contigo sobre tu pedido
• Enviarte actualizaciones del producto (solo si das tu consentimiento)

3. Protección de datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.

4. Compartir información
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu pago o cumplir con obligaciones legales.

5. Tus derechos
Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, contáctanos a través de nuestro correo electrónico.

6. Cookies
Nuestro sitio puede utilizar cookies esenciales para mejorar tu experiencia de navegación. No utilizamos cookies de seguimiento publicitario.

7. Contacto
Si tienes preguntas sobre esta política de privacidad, puedes escribirnos a: epikkastore@gmail.com
`.trim();
const termsContent = `
Última actualización: abril 2026

Al comprar y descargar la guía digital "Primeros Bocados", aceptas los siguientes términos y condiciones.

1. Producto digital
"Primeros Bocados" es un producto digital en formato PDF. Al completar tu compra, recibirás acceso inmediato para descargar el ebook y los bonos incluidos (Plan Semanal de Introducción, La Despensa Consciente y 30 Recetas Esenciales).

2. Uso personal
El contenido de esta guía es para uso personal y familiar. Queda prohibida su reproducción, distribución, reventa o compartición total o parcial sin autorización expresa.

3. Propiedad intelectual
Todo el contenido de "Primeros Bocados" — incluyendo textos, diseño, ilustraciones y recursos — está protegido por derechos de autor. Todos los derechos están reservados.

4. Naturaleza informativa
Esta guía tiene fines informativos y educativos. No sustituye el consejo médico profesional. Siempre consulta con tu pediatra o profesional de salud antes de tomar decisiones sobre la alimentación de tu bebé.

5. Política de reembolso
Aceptamos solicitudes de devolución dentro de los primeros 7 días naturales desde la fecha de compra. Pasado ese plazo, no se aceptarán reembolsos. Para solicitar una devolución, escríbenos a epikkastore@gmail.com indicando tu número de pedido.

6. Limitación de responsabilidad
Epikka Store no se hace responsable de los resultados individuales obtenidos al seguir las recomendaciones de la guía. Cada bebé y familia es diferente.

7. Modificaciones
Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación.

8. Contacto
Para cualquier consulta sobre estos términos, escríbenos a: epikkastore@gmail.com
`.trim();
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: ModalProps) {
  return <AnimatePresence data-id="element-141">
      {isOpen && <>
          <motion.div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-142" />
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-143">
            <motion.div className="bg-cream rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} data-id="element-144">
              <div className="flex items-center justify-between p-6 border-b border-sand" data-id="element-145">
                <h3 className="text-2xl font-serif text-charcoal" data-id="element-146">{title}</h3>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-sand transition-colors" data-id="element-147">
                  <X className="w-5 h-5 text-warmGray" data-id="element-148" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto" data-id="element-149">
                <div className="text-warmGray leading-relaxed whitespace-pre-line text-sm" data-id="element-150">
                  {content}
                </div>
              </div>
              <div className="p-4 border-t border-sand text-center" data-id="element-151">
                <button onClick={onClose} className="bg-olive hover:bg-olive/90 text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm" data-id="element-152">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'contact' | null>(null);
  return <>
      <div className="bg-sand py-6" data-id="element-153">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-center gap-3 text-center" data-id="element-154">
          <svg className="w-5 h-5 text-olive shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-155">
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" data-id="element-156" />
            <path d="m9 12 2 2 4-4" data-id="element-157" />
          </svg>
          <p className="text-sm text-warmGray" data-id="element-158">
            <span className="font-medium text-charcoal" data-id="element-159">
              Garantía de reembolso de 7 días.
            </span>{' '}
            Si no estás satisfecho/a, te devolvemos tu dinero sin preguntas.
          </p>
        </div>
      </div>

      <footer className="bg-charcoal text-cream/80 py-12" data-id="element-160">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-161">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-162">
            <div className="flex items-center gap-3" data-id="element-163">
              <img src="https://cdn.magicpatterns.com/uploads/rgU7gUPBFU366a1fsk78Bg/Gemini_Generated_Image_voy4efvoy4efvoy4.png" alt="Epikka Store" className="w-10 h-10 object-contain invert" data-id="element-164" />
              <span className="font-serif text-2xl text-cream" data-id="element-165">
                Epikka Store
              </span>
            </div>

            <div className="flex gap-6 text-sm" data-id="element-166">
              <a href="#" className="hover:text-cream transition-colors" data-id="element-167">
                Inicio
              </a>
              <a href="#" className="hover:text-cream transition-colors" data-id="element-168">
                Tienda
              </a>
              <a href="#faq" className="hover:text-cream transition-colors" onClick={e => {
              e.preventDefault();
              document.getElementById('faq')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-169">
                Preguntas frecuentes
              </a>
              <button onClick={() => setActiveModal('contact')} className="hover:text-cream transition-colors" data-id="element-170">
                Contacto
              </button>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-cream/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-cream/50" data-id="element-171">
            <p data-id="element-172">
              © {new Date().getFullYear()} Epikka Store. Todos los derechos
              reservados.
            </p>
            <div className="flex gap-4" data-id="element-173">
              <button onClick={() => setActiveModal('privacy')} className="hover:text-cream transition-colors" data-id="element-174">
                Política de Privacidad
              </button>
              <button onClick={() => setActiveModal('terms')} className="hover:text-cream transition-colors" data-id="element-175">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" content={privacyContent} data-id="element-176" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={termsContent} data-id="element-177" />

      <AnimatePresence data-id="element-178">
        {activeModal === 'contact' && <>
            <motion.div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} data-id="element-179" />
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-180">
              <motion.div className="bg-cream rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" initial={{
            scale: 0.95,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3
          }} onClick={e => e.stopPropagation()} data-id="element-181">
                <div className="flex items-center justify-between p-6 border-b border-sand" data-id="element-182">
                  <h3 className="text-2xl font-serif text-charcoal" data-id="element-183">
                    Contacto
                  </h3>
                  <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-sand transition-colors" data-id="element-184">
                    <X className="w-5 h-5 text-warmGray" data-id="element-185" />
                  </button>
                </div>
                <div className="p-8 space-y-6 text-center" data-id="element-186">
                  <p className="text-warmGray leading-relaxed" data-id="element-187">
                    ¿Tienes alguna pregunta o necesitas ayuda? No dudes en
                    contactarnos.
                  </p>
                  <div className="space-y-4" data-id="element-188">
                    <a href="mailto:epikkastore@gmail.com" className="flex items-center justify-center gap-3 bg-white border border-sand rounded-xl px-6 py-4 hover:shadow-md transition-shadow" data-id="element-189">
                      <svg className="w-5 h-5 text-olive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-190">
                        <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-191" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-192" />
                      </svg>
                      <span className="text-charcoal font-medium" data-id="element-193">
                        epikkastore@gmail.com
                      </span>
                    </a>
                    <a href="https://www.facebook.com/profile.php?id=61572064552166" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-white border border-sand rounded-xl px-6 py-4 hover:shadow-md transition-shadow" data-id="element-194">
                      <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor" data-id="element-195">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-196" />
                      </svg>
                      <span className="text-charcoal font-medium" data-id="element-197">
                        Síguenos en Facebook
                      </span>
                    </a>
                  </div>
                </div>
                <div className="p-4 border-t border-sand text-center" data-id="element-198">
                  <button onClick={() => setActiveModal(null)} className="bg-olive hover:bg-olive/90 text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm" data-id="element-199">
                    Cerrar
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}